import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import swal from "sweetalert";

const KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";
const money = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";
};

const Icon = ({ name, size = 20 }) => {
  const paths = {
    wallet: <><rect x="3" y="5" width="18" height="15" rx="3"/><path d="M3 9h18"/><path d="M16 14h2"/></>,
    swap: <><path d="M4 7h16m-4-4 4 4-4 4M20 17H4m4-4-4 4 4 4"/></>,
    shield: <><path d="M12 2 4 5v6c0 5 3.3 8.5 8 11 4.7-2.5 8-6 8-11V5l-8-3Z"/><path d="m9 12 2 2 4-4"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    lock: <><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></>,
    eye: <><path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></>,
    eyeOff: <><path d="m3 3 18 18M10.6 6.1A12 12 0 0 1 12 6c6.4 0 10 6 10 6a15 15 0 0 1-3.1 3.6M6.2 6.9C3.5 8.6 2 12 2 12s3.6 6 10 6c1.6 0 3-.4 4.2-1"/></>,
    info: <><circle cx="12" cy="12" r="10"/><path d="M12 11v6m0-10h.01"/></>,
    arrow: <><path d="M5 12h14m-6-6 6 6-6 6"/></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
};

export default function Checkout() {
  const [balance, setBalance] = useState(null);
  const [form, setForm] = useState({ amount: "", password: "" });
  const [errors, setErrors] = useState({});
  const [accepted, setAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const refreshBalance = useCallback(async (signal) => {
    if (!localStorage.getItem("auth_token")) return;
    try {
      const response = await axios.post("/apps-profile", {
        key_s: KEY,
        currency: localStorage.getItem("selectedCurrency"),
      }, { signal });
      if (response.data?.data) setBalance(response.data.data.balance);
    } catch (error) {
      if (error.code !== "ERR_CANCELED" && error.name !== "CanceledError") {
        console.error("Unable to retrieve wallet balance", error);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    refreshBalance(controller.signal);
    return () => controller.abort();
  }, [refreshBalance]);

  const onInput = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const amountNumber = Number(form.amount);
  const validAmount = form.amount.trim() !== "" && Number.isFinite(amountNumber) && amountNumber > 0;
  const exceedsBalance = validAmount && balance !== null && amountNumber > Number(balance);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    if (!localStorage.getItem("auth_token")) {
      swal("Session Expired", "Please sign in before converting funds.", "warning");
      return;
    }
    const nextErrors = {};
    if (!validAmount) nextErrors.amount = "Enter a valid amount greater than zero.";
    if (exceedsBalance) nextErrors.amount = "Amount exceeds your available balance.";
    if (!form.password) nextErrors.password = "Please enter your account password.";
    if (!accepted) nextErrors.terms = "Please accept the terms and conditions.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const res = await axios.post("/convert-balance", {
        amount: form.amount,
        password: form.password,
        key_s: KEY,
      });
      if (res.data?.status === "success") {
        setForm({ amount: "", password: "" });
        setErrors({});
        setAccepted(false);
        refreshBalance();
        swal("Success", res.data.message || "Balance converted successfully.", "success");
      } else if (res.data?.status === "fail") {
        swal("Warning", res.data.message || "Conversion could not be completed.", "warning");
      } else {
        setErrors(res.data?.validation_error || {});
        if (res.data?.message && !res.data?.validation_error) swal("Warning", res.data.message, "warning");
      }
    } catch (error) {
      if (error.response?.data?.validation_error) setErrors(error.response.data.validation_error);
      swal("Request Failed", error.response?.data?.message || "Please try again later.", "error");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const errorText = (value) => Array.isArray(value) ? value.join(" ") : typeof value === "string" ? value : "";

  return <>
    <style>{`
      html,body,#root{background:#00191D!important;min-height:100%;}
      .rmcv,.rmcv *{box-sizing:border-box}
      .rmcv{--navy:#00191D;--cyan:#16B8C7;--muted:#9DB6BA;min-height:100vh;width:100%;padding:20px 4px 40px;background:#00191D;color:#fff;font-family:inherit;font-size:14px;line-height:1.55;overflow-x:hidden}
      .rmcv button,.rmcv input{font:inherit}
      .rmcv-shell{width:100%;max-width:1150px;margin:0 auto}
      .rmcv-top{border:1px solid #164249;border-radius:20px;background:linear-gradient(115deg,#062B32,#00191D 70%);padding:24px;display:flex;align-items:center;justify-content:space-between;gap:20px;animation:rmcv-in .35s ease-out both}
      .rmcv-eyebrow{color:#68DBE2;font-size:12px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;margin:0 0 6px}
      .rmcv-title{font-size:clamp(22px,3vw,30px);font-weight:800;line-height:1.2;margin:0 0 9px;color:#fff}
      .rmcv-description{margin:0;color:#B1C8CC;max-width:560px}
      .rmcv-top-icon{width:66px;height:66px;display:grid;place-items:center;border-radius:20px;background:linear-gradient(140deg,#16B8C7,#0A7686);color:#fff;flex:none;box-shadow:0 8px 24px #16b8c72a}
      .rmcv-grid{display:grid;grid-template-columns:minmax(0,1.1fr) minmax(0,.9fr);gap:16px;margin-top:16px;align-items:start}
      .rmcv-main{border:1px solid #215159;border-radius:20px;background:linear-gradient(155deg,#092F36,#042229 70%);padding:22px;min-width:0;animation:rmcv-in .45s ease-out both}
      .rmcv-side{display:grid;gap:16px;min-width:0}
      .rmcv-balance{border-radius:20px;padding:22px;background:#fff;color:#07272C;border:1px solid #E3EEF0;animation:rmcv-in .5s ease-out both}
      .rmcv-balance-top{display:flex;align-items:center;gap:12px;margin-bottom:20px}
      .rmcv-balance-icon{width:46px;height:46px;border-radius:14px;background:#E7F8F9;color:#098C9A;display:grid;place-items:center}
      .rmcv-balance-label{font-size:13px;color:#5E7C83;font-weight:650}
      .rmcv-balance-value{font-size:clamp(27px,3vw,38px);line-height:1.1;font-weight:850;overflow-wrap:anywhere;font-variant-numeric:tabular-nums}
      .rmcv-balance-foot{margin-top:17px;padding-top:13px;border-top:1px solid #E5EFF0;color:#5A777D;font-size:12px}
      .rmcv-summary{border:1px solid #215159;border-radius:20px;background:#05272E;padding:20px;animation:rmcv-in .55s ease-out both}
      .rmcv-panel-heading{display:flex;align-items:center;gap:10px;margin:0 0 20px;font-size:17px;font-weight:800;color:#fff}
      .rmcv-heading-icon{width:34px;height:34px;border-radius:10px;background:#0C4149;color:#58DBE3;display:grid;place-items:center}
      .rmcv-subtext{color:#A7C3C8;font-size:12px;margin:-11px 0 20px}
      .rmcv-field{margin-bottom:18px}
      .rmcv-label{display:block;margin:0 0 8px;color:#E6F6F7;font-weight:650;font-size:14px}
      .rmcv-input-wrap{display:flex;align-items:center;position:relative}
      .rmcv-input{width:100%;height:50px;border:1px solid #315C63;border-radius:12px;background:#00191D;color:#fff;outline:0;padding:0 15px;font-size:14px;transition:border-color .18s,box-shadow .18s}
      .rmcv-input::placeholder{color:#78979D}
      .rmcv-input:focus{border-color:#16B8C7;box-shadow:0 0 0 3px #16b8c723}
      .rmcv-input[type=number]{appearance:textfield;-moz-appearance:textfield}
      .rmcv-input::-webkit-outer-spin-button,.rmcv-input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
      .rmcv-prefix{position:absolute;left:15px;color:#74DDE3;font-weight:800}
      .rmcv-input.has-prefix{padding-left:33px}
      .rmcv-input.has-action{padding-right:55px}
      .rmcv-eye{position:absolute;right:5px;top:5px;width:40px;height:40px;border:0;border-radius:9px;background:transparent;color:#94C7CB;display:grid;place-items:center;cursor:pointer}
      .rmcv-eye:hover{background:#104049;color:#fff}
      .rmcv-field-error{font-size:12px;color:#FF929B;margin-top:6px;display:block}
      .rmcv-shortcut{display:flex;flex-wrap:wrap;gap:8px;margin-top:9px}
      .rmcv-chip{border:1px solid #2D6068;background:#0B343C;color:#B6E9EC;border-radius:8px;padding:5px 12px;cursor:pointer;transition:background .18s,border-color .18s}
      .rmcv-chip:hover{background:#10515B;border-color:#16B8C7}
      .rmcv-terms{display:flex;align-items:flex-start;gap:10px;margin:20px 0;color:#C2D9DC;cursor:pointer}
      .rmcv-terms input{width:17px;height:17px;margin-top:2px;accent-color:#16B8C7;flex:none}
      .rmcv-submit{width:100%;min-height:51px;border:0;border-radius:12px;display:flex;align-items:center;justify-content:center;gap:10px;background:linear-gradient(110deg,#16B8C7,#0A8395);color:#fff;font-weight:800;cursor:pointer;transition:transform .2s,filter .2s,box-shadow .2s;box-shadow:0 8px 18px #16b8c71f}
      .rmcv-submit:hover:not(:disabled){transform:translateY(-2px);filter:brightness(1.08);box-shadow:0 11px 24px #16b8c738}
      .rmcv-submit:disabled{opacity:.65;cursor:not-allowed}
      .rmcv-row{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:13px 0;border-bottom:1px solid #214B53}
      .rmcv-row:last-child{border:0}
      .rmcv-row-label{color:#B4CFD2}
      .rmcv-row-value{color:#fff;font-weight:750;text-align:right;font-variant-numeric:tabular-nums;overflow-wrap:anywhere}
      .rmcv-highlight{color:#58DDE5}
      .rmcv-note{margin-top:16px;display:flex;align-items:flex-start;gap:10px;padding:14px;border-radius:13px;background:#0A333A;border:1px solid #215159;color:#B9D3D6;font-size:12px}
      .rmcv-note svg{color:#63D6DD;flex:none}
      @keyframes rmcv-in{from{opacity:.4;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
      @media(max-width:800px){.rmcv-grid{grid-template-columns:1fr}.rmcv-side{grid-row:1}.rmcv-top{padding:18px}.rmcv-main,.rmcv-balance,.rmcv-summary{padding:17px}}
      @media(max-width:480px){.rmcv{padding-top:8px}.rmcv-top{padding:15px;border-radius:15px}.rmcv-top-icon{width:46px;height:46px;border-radius:12px}.rmcv-top-icon svg{width:24px}.rmcv-grid{gap:10px;margin-top:10px}.rmcv-main,.rmcv-balance,.rmcv-summary{border-radius:15px;padding:15px}.rmcv-title{font-size:22px}.rmcv-input{height:48px}}
      @media(prefers-reduced-motion:reduce){.rmcv *, .rmcv *::before,.rmcv *::after{animation:none!important;transition:none!important}}
    `}</style>
    <main className="rmcv">
      <div className="rmcv-shell">
        <header className="rmcv-top">
          <div>
            <p className="rmcv-eyebrow">RM CROWN MARKET · Wallet Services</p>
            <h1 className="rmcv-title">Convert Balance</h1>
            <p className="rmcv-description">Move funds from your Main Wallet to your Principal Wallet using your account password.</p>
          </div>
          <div className="rmcv-top-icon"><Icon name="swap" size={31}/></div>
        </header>
        <div className="rmcv-grid">
          <section className="rmcv-main" aria-labelledby="rmcv-form-title">
            <h2 className="rmcv-panel-heading" id="rmcv-form-title"><span className="rmcv-heading-icon"><Icon name="swap" size={18}/></span> New Conversion</h2>
            <p className="rmcv-subtext">Enter the amount and confirm your account credentials.</p>
            <form onSubmit={onSubmit}>
              <div className="rmcv-field">
                <label htmlFor="rmcv-amount" className="rmcv-label">Conversion Amount (USD)</label>
                <div className="rmcv-input-wrap"><span className="rmcv-prefix">$</span><input id="rmcv-amount" className="rmcv-input has-prefix" name="amount" type="number" min="0.01" step="any" inputMode="decimal" placeholder="Enter amount" autoComplete="off" value={form.amount} onChange={onInput} required aria-invalid={Boolean(errors.amount)}/></div>
                {errors.amount && <span className="rmcv-field-error">{errorText(errors.amount)}</span>}
                <div className="rmcv-shortcut"><button type="button" className="rmcv-chip" onClick={() => {setForm(v => ({...v,amount:balance === null ? v.amount : String((Math.max(0,Number(balance)) * .25).toFixed(2))}));setErrors(v=>({...v,amount:undefined}));}} disabled={balance === null}>25%</button><button type="button" className="rmcv-chip" onClick={() => {setForm(v => ({...v,amount:balance === null ? v.amount : String((Math.max(0,Number(balance)) * .5).toFixed(2))}));setErrors(v=>({...v,amount:undefined}));}} disabled={balance === null}>50%</button><button type="button" className="rmcv-chip" onClick={() => {setForm(v => ({...v,amount:balance === null ? v.amount : String(balance)}));setErrors(v=>({...v,amount:undefined}));}} disabled={balance === null}>Max</button></div>
              </div>
              <div className="rmcv-field">
                <label htmlFor="rmcv-password" className="rmcv-label">Account Password</label>
                <div className="rmcv-input-wrap"><input id="rmcv-password" className="rmcv-input has-action" name="password" type={showPassword ? "text" : "password"} placeholder="Enter your password" autoComplete="current-password" value={form.password} onChange={onInput} required aria-invalid={Boolean(errors.password)}/><button type="button" className="rmcv-eye" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(v=>!v)}><Icon name={showPassword ? "eyeOff" : "eye"}/></button></div>
                {errors.password && <span className="rmcv-field-error">{errorText(errors.password)}</span>}
              </div>
              <label className="rmcv-terms"><input type="checkbox" checked={accepted} onChange={e=>{setAccepted(e.target.checked);setErrors(v=>({...v,terms:undefined}));}}/><span>I accept the Terms &amp; Conditions for this conversion.</span></label>
              {errors.terms && <span className="rmcv-field-error">{errorText(errors.terms)}</span>}
              <button className="rmcv-submit" type="submit" disabled={submitting}><Icon name="swap" size={19}/>{submitting ? "Processing..." : "Convert Now"}<Icon name="arrow" size={18}/></button>
            </form>
          </section>
          <aside className="rmcv-side">
            <section className="rmcv-balance" aria-label="Available balance">
              <div className="rmcv-balance-top"><div className="rmcv-balance-icon"><Icon name="wallet" size={23}/></div><div><div className="rmcv-balance-label">Main Wallet</div><strong>Available Balance</strong></div></div>
              <div className="rmcv-balance-value">{balance === null ? "—" : `$${money(balance)}`}</div>
              <div className="rmcv-balance-foot">Balance fetched from your account profile</div>
            </section>
            <section className="rmcv-summary" aria-labelledby="rmcv-summary-title">
              <h2 className="rmcv-panel-heading" id="rmcv-summary-title"><span className="rmcv-heading-icon"><Icon name="check" size={19}/></span> Conversion Summary</h2>
              <div className="rmcv-row"><span className="rmcv-row-label">Conversion Rate</span><strong className="rmcv-row-value">100%</strong></div>
              <div className="rmcv-row"><span className="rmcv-row-label">Fee Percentage</span><strong className="rmcv-row-value">0%</strong></div>
              <div className="rmcv-row"><span className="rmcv-row-label">Amount After Fees</span><strong className="rmcv-row-value rmcv-highlight">${money(validAmount ? amountNumber : 0)}</strong></div>
              <div className="rmcv-row"><span className="rmcv-row-label">Destination</span><strong className="rmcv-row-value">Principal Wallet</strong></div>
            </section>
          </aside>
        </div>
        <div className="rmcv-note"><Icon name="shield" size={19}/><span>Review the amount and wallet details before confirming. The final conversion result is determined by the server.</span></div>
      </div>
    </main>
  </>;
}
